import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import ClaimNotificationCommunicationSuggestion from '~/components/ClaimNotificationsCard/ClaimNotificationCommunicationSuggestion';
import type {
  ClaimNotificationModel,
  CommunicationClaimNotificationModel,
  CommunicationSuggestionClaimNotificationModel,
  FinancialRequestDecisionClaimNotificationModel,
  NotificationTypesUnion,
  ReminderClaimNotificationModel,
  UpdateNotification,
} from '~/components/ClaimNotificationsCard/types';
import ClaimOwnerChangedNotificationDisplay from '~/components/Notifications/ClaimOwnerChangeNotificationDisplay';
import GenericNotificationHeader from '~/components/Notifications/GenericNotificationHeader';
import type { ClaimPolymorph } from '~/components/types/claim-types';

import ViewCommunicationCardContainer from '../communications/ViewCommunicationCardContainer';
import PaymentRequestDynamicContainer from '../exposures/PaymentRequestContainer/PaymentRequestDynamicContainer';
import { ReserveChangeRequestDynamicContainer } from '../exposures/ReserveChangeRequestContainer';
import { InternalCommunicationDisplay } from '../InternalCommunication';
import LoadingDialog from '../LoadingDialog';
import { ReminderDisplay } from '../ReminderNotificationContainer';
import { useCliveConfigOptions } from '../SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import useDataFetcher from '../useDataFetcher';

import CliveReminderNotificationContent from './CliveReminderNotificationContent';

interface ViewNotificationCardProps {
  notification: Pick<ClaimNotificationModel, 'id' | 'claim_id'>;
  onClose: () => void;
  onUpdate: () => void;
  onMarkAsUnread: (notificationId: number) => void;
  onUpdateNotification: UpdateNotification;
}

const ViewNotificationCard: React.FC<ViewNotificationCardProps> = ({
  notification,
  onUpdate,
  onClose,
  onMarkAsUnread,
  onUpdateNotification,
}) => {
  const {
    isLoading,
    isError,
    data: fullNotification,
  } = useDataFetcher<NotificationTypesUnion>(
    notification.claim_id
      ? `/api/v1/claims/${notification.claim_id}/notifications/${notification.id}`
      : `/api/v1/claims/claims_notifications/${notification.id}`
  );
  const [optimisticNotification, setOptimisticNotification] = useState<NotificationTypesUnion>();
  const { isCliveEnabledForOrganization } = useCliveConfigOptions();

  useEffect(() => {
    if (fullNotification) {
      setOptimisticNotification(fullNotification);
    }
  }, [fullNotification]);

  const populateCommunicationClaimNotification = (
    communicationClaimNotification: CommunicationClaimNotificationModel | CommunicationSuggestionClaimNotificationModel
  ) => {
    const communication = communicationClaimNotification.communication;

    return {
      ...communicationClaimNotification,
      viewComponent: ViewCommunicationCardContainer,
      viewComponentProps: {
        claimId: communicationClaimNotification.communication?.claim_id,
        isDialog: true,
        startIsEditing: false,
        communicationId: communication?.id,
        showClive: true,
        onUpdate,
        onClose,
      },
    };
  };

  const populateFinancialRequestDecisionClaimNotification = (
    financialClaimNotification: FinancialRequestDecisionClaimNotificationModel
  ) => {
    let viewComponent = undefined;
    let viewComponentProps = undefined;

    const financialRequest = financialClaimNotification.financial_request;

    switch (financialRequest.type) {
      case 'reserve_change_request':
        viewComponent = ReserveChangeRequestDynamicContainer;
        viewComponentProps = { reserveChangeRequest: financialRequest };
        break;
      case 'payment_request':
        viewComponent = PaymentRequestDynamicContainer;
        viewComponentProps = { paymentRequest: financialRequest };
        break;
      default:
        throw Error(`Unknown financial request type: ${financialRequest.type}`);
    }

    return {
      ...financialClaimNotification,
      title: financialClaimNotification.title,
      viewComponent,
      viewComponentProps: {
        ...viewComponentProps,
        claimId: financialClaimNotification.claim_id,
        exposureId: financialClaimNotification.exposure_id,
        payableWithReserveId: financialRequest.payable_with_reserve_id,
        onUpdate,
        cardDialogProps: { isDialog: true, maxWidth: 'sm', fullWidth: true },
        onClose,
      },
    };
  };

  const getCliveReminderAdditionalContent = (
    reminderNotification: ReminderClaimNotificationModel
  ): ((claim: ClaimPolymorph) => ReactElement | null) | null => {
    if (!isCliveEnabledForOrganization()) {
      return null;
    }

    const AdditionalContent = (claim: ClaimPolymorph) => (
      <CliveReminderNotificationContent
        reminder={reminderNotification}
        claim={claim}
        onClose={onClose}
        onUpdate={onUpdate}
        onUpdateNotification={onUpdateNotification}
      />
    );
    AdditionalContent.displayName = 'CliveReminderAdditionalContent';

    return AdditionalContent;
  };

  const populateClaimNotification = (claimNotification: NotificationTypesUnion) => {
    switch (claimNotification.type) {
      case 'communication_suggestion_claim_notification_model':
        return claimNotification.communication_id
          ? populateCommunicationClaimNotification(claimNotification)
          : {
              ...claimNotification,
              notification_date: claimNotification.due_date,
              viewComponent: ClaimNotificationCommunicationSuggestion,
              // The registry instantiates the editor outside this subtree, so it owns the header
              // itself -- a pre-rendered element would freeze its chips at registration time.
              viewComponentProps: {
                onUpdate,
                claimNotification,
                onClose,
                onMarkAsUnread,
                onUpdateNotification,
              },
            };
      case 'communication_claim_notification':
        return populateCommunicationClaimNotification(claimNotification);
      case 'financial_request_decision_claim_notification':
        return populateFinancialRequestDecisionClaimNotification(claimNotification);
      case 'claim_owner_change_claim_notification':
        return {
          ...claimNotification,
          viewComponent: ClaimOwnerChangedNotificationDisplay,
          viewComponentProps: { notification: claimNotification, onClose },
        };
      case 'reminder_claim_notification':
        return {
          ...claimNotification,
          notification_date: claimNotification.due_date,
          viewComponent: ReminderDisplay,
          viewComponentProps: {
            onUpdate,
            reminder: claimNotification,
            open: true,
            onClose,
            AdditionalContent: getCliveReminderAdditionalContent(claimNotification),
          },
        };
      case 'internal_communication_claim_notification':
        return {
          ...claimNotification,
          viewComponent: InternalCommunicationDisplay,
          viewComponentProps: {
            showRecipient: true,
            onUpdate,
            internalCommunication: claimNotification.internal_communication,
            open: true,
            onClose,
          },
        };
    }
  };

  if (isLoading || isError || !fullNotification || !optimisticNotification) {
    return (
      <LoadingDialog
        isError={isError || (!isLoading && !fullNotification)}
        onClose={onClose}
        track="View Claim Notification"
        delay={500}
      />
    );
  }

  const populatedClaimNotification = populateClaimNotification(fullNotification);

  return (
    <populatedClaimNotification.viewComponent
      {...populatedClaimNotification.viewComponentProps}
      AdditionalDialogHeader={
        <GenericNotificationHeader
          notification={optimisticNotification}
          onClose={onClose}
          onMarkAsUnread={onMarkAsUnread}
          onUpdateNotification={(claimNotificationId: number, updates: Partial<UpdateNotification>) => {
            setOptimisticNotification({ ...optimisticNotification, ...updates });
            onUpdateNotification(claimNotificationId, updates);
          }}
        />
      }
    />
  );
};

export default ViewNotificationCard;
